import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import ShieldIcon from '@mui/icons-material/Shield';
import PolicyIcon from '@mui/icons-material/Policy';
import { useParams, useSearchParams } from 'react-router-dom';
import SignatureCapture from './SignatureCapture';
import { fetchPublicForm, signPublicForm } from '../../services/formsApi';

const DEFAULT_TIME_FORMAT = (isoString) => {
  try {
    return new Date(isoString).toLocaleString();
  } catch (error) {
    return isoString;
  }
};

const FIELD_TYPE_COMPONENTS = {
  text: { multiline: false },
  email: { multiline: false, type: 'email' },
  number: { multiline: false, type: 'number' },
  textarea: { multiline: true, minRows: 4 },
  date: { multiline: false, type: 'date', InputLabelProps: { shrink: true } },
  radio: { multiline: false },
  select: { multiline: false }
};

const PublicFormSigning = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [template, setTemplate] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [signatureValues, setSignatureValues] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [ackError, setAckError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [ipAddress, setIpAddress] = useState(null);
  const signatureRefs = useRef({});

  const accessHint = searchParams.get('email') || searchParams.get('recipient');

  useEffect(() => {
    let isMounted = true;
    const loadIpAddress = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json', {
          cache: 'no-store'
        });
        if (!response.ok) {
          throw new Error('Unable to determine IP');
        }
        const data = await response.json();
        if (isMounted) {
          setIpAddress(data.ip);
        }
      } catch (ipError) {
        // Non-fatal; the API will still capture remote address from the request
        if (isMounted) {
          setIpAddress(null);
        }
      }
    };
    loadIpAddress();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadForm = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await fetchPublicForm(token);
        if (!payload) {
          if (isMounted) {
            setError('This signing session could not be found or has expired.');
          }
          return;
        }

        const submissionData = payload.submission || payload.form || payload;
        const templateData =
          payload.template || submissionData?.template || payload.schema?.template || null;

        if (isMounted) {
          setSubmission(submissionData);
          setTemplate(templateData);
          const prefilled =
            payload.prefill ||
            submissionData?.responses ||
            submissionData?.metadata?.responses ||
            {};
          setFormValues(prefilled || {});

          const signaturePrefill =
            payload.signature_hashes ||
            submissionData?.metadata?.signature_hashes ||
            {};
          setSignatureValues(signaturePrefill || {});
        }
      } catch (fetchError) {
        console.error(fetchError);
        if (isMounted) {
          setError(fetchError.message || 'Unable to load signing session.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (token) {
      loadForm();
    } else {
      setError('Missing signing token.');
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [token]);

  const schema = useMemo(() => {
    if (template?.schema) {
      return template.schema;
    }
    if (submission?.schema) {
      return submission.schema;
    }
    return {};
  }, [template, submission]);

  const orderedFields = useMemo(() => {
    const fields = schema?.fields || [];
    return [...fields].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [schema]);

  const attachments = useMemo(() => schema?.attachments || [], [schema]);

  const updateField = (fieldKey, value) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleSignatureChange = (fieldKey) => (dataUrl) => {
    setSignatureValues((prev) => ({
      ...prev,
      [fieldKey]: dataUrl
    }));
    setValidationErrors((prev) => {
      if (!prev[fieldKey]) {
        return prev;
      }
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  const validateForm = () => {
    const nextErrors = {};

    orderedFields.forEach((field) => {
      if (!field) {
        return;
      }
      const key = field.field_key || field.id || field.name;
      if (!key) {
        return;
      }

      if (field.field_type === 'signature') {
        const hasSignature = signatureValues[key];
        if (field.required && !hasSignature) {
          nextErrors[key] = 'Signature required.';
        }
        return;
      }

      if (field.field_type === 'checkbox') {
        const checked = Boolean(formValues[key]);
        if (field.required && !checked) {
          nextErrors[key] = 'This acknowledgement is required.';
        }
        return;
      }

      const value = formValues[key];
      if (field.required && (value === undefined || value === null || value === '')) {
        nextErrors[key] = 'This field is required.';
      }
    });

    if (!acknowledged) {
      setAckError('You must agree to electronically sign this document.');
    } else {
      setAckError(null);
    }

    setValidationErrors(nextErrors);

    return Object.keys(nextErrors).length === 0 && acknowledged;
  };

  const buildAuditMetadata = () => {
    const tz = (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (timezoneError) {
        return null;
      }
    })();

    return {
      signedAt: new Date().toISOString(),
      ipAddress,
      userAgent: typeof window !== 'undefined' ? navigator.userAgent : null,
      language: typeof navigator !== 'undefined' ? navigator.language : null,
      platform: typeof navigator !== 'undefined' ? navigator.platform : null,
      timeZone: tz,
      screen: typeof window !== 'undefined' ? { width: window.innerWidth, height: window.innerHeight } : null,
      referrer: typeof document !== 'undefined' ? document.referrer : null,
      location: typeof window !== 'undefined' ? window.location.href : null
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    const signatureEntries = Object.entries(signatureValues).filter(([, value]) => Boolean(value));
    if (signatureEntries.length === 0) {
      const firstSignatureField = orderedFields.find((field) => field?.field_type === 'signature');
      if (firstSignatureField) {
        const signatureKey =
          firstSignatureField.field_key || firstSignatureField.id || 'signature';
        setValidationErrors((prev) => ({
          ...prev,
          [signatureKey]: 'Capture your signature to continue.'
        }));
      }
      return;
    }

    setSubmitting(true);
    setError(null);

    const audit = buildAuditMetadata();
    const payload = {
      signature_hash: signatureEntries[0][1],
      signature_hashes: signatureValues,
      form_values: formValues,
      metadata: {
        responses: formValues,
        acknowledgement: acknowledged,
        audit,
        signature_fields: signatureEntries.map(([fieldKey]) => fieldKey)
      }
    };

    const signerName =
      formValues.full_name ||
      formValues.name ||
      formValues.signer_name ||
      formValues.signature_name ||
      null;
    if (signerName) {
      payload.metadata.signer_name = signerName;
    }
    if (accessHint) {
      payload.metadata.recipient_hint = accessHint;
    }

    try {
      await signPublicForm(token, payload);
      setCompleted(true);
    } catch (submitError) {
      console.error(submitError);
      setError(submitError.message || 'Unable to submit your signature.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field, index) => {
    if (!field) {
      return null;
    }

    const key = field.field_key || field.id || `field-${index}`;
    const label = field.label || field.name || `Field ${index + 1}`;
    const helper = field.description || field?.config?.helpText;
    const errorMessage = validationErrors[key];

    if (field.field_type === 'signature') {
      if (!signatureRefs.current[key]) {
        signatureRefs.current[key] = React.createRef();
      }

      return (
        <Box key={key} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {label}
            {field.required ? ' *' : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use your mouse, trackpad, or finger (on touch devices) to sign below.
          </Typography>
          <SignatureCapture
            ref={signatureRefs.current[key]}
            height={240}
            onChange={handleSignatureChange(key)}
          />
          {errorMessage && (
            <FormHelperText error sx={{ mt: 1 }}>
              {errorMessage}
            </FormHelperText>
          )}
        </Box>
      );
    }

    if (field.field_type === 'checkbox') {
      return (
        <Box key={key} sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(formValues[key])}
                onChange={(event) => updateField(key, event.target.checked)}
                required={field.required}
              />
            }
            label={
              <Typography variant="body2">
                {label}
                {field.required ? ' *' : ''}
              </Typography>
            }
          />
          {errorMessage && (
            <FormHelperText error sx={{ ml: 3 }}>
              {errorMessage}
            </FormHelperText>
          )}
        </Box>
      );
    }

    const optionList =
      Array.isArray(field.options) && field.options.length > 0
        ? field.options
        : Array.isArray(field?.config?.options)
        ? field.config.options
        : [];

    if (field.field_type === 'radio' && optionList.length > 0) {
      return (
        <FormControl key={key} sx={{ mb: 3 }} error={Boolean(errorMessage)}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
            {field.required ? ' *' : ''}
          </Typography>
          <RadioGroup
            value={formValues[key] ?? ''}
            onChange={(event) => updateField(key, event.target.value)}
          >
            {optionList.map((option) => {
              const optionValue = option.value ?? option.id ?? option.label ?? option;
              const optionLabel = option.label ?? optionValue;
              return (
                <FormControlLabel
                  key={`${key}-${optionValue}`}
                  value={optionValue}
                  control={<Radio />}
                  label={optionLabel}
                />
              );
            })}
          </RadioGroup>
          {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
        </FormControl>
      );
    }

    if (field.field_type === 'select' && optionList.length > 0) {
      return (
        <TextField
          key={key}
          select
          fullWidth
          label={`${label}${field.required ? ' *' : ''}`}
          value={formValues[key] ?? ''}
          onChange={(event) => updateField(key, event.target.value)}
          helperText={errorMessage || helper}
          error={Boolean(errorMessage)}
          sx={{ mb: 3 }}
        >
          <MenuItem value="" disabled>
            Select…
          </MenuItem>
          {optionList.map((option) => {
            const optionValue = option.value ?? option.id ?? option.label ?? option;
            const optionLabel = option.label ?? optionValue;
            return (
              <MenuItem key={`${key}-${optionValue}`} value={optionValue}>
                {optionLabel}
              </MenuItem>
            );
          })}
        </TextField>
      );
    }

    const componentProps = FIELD_TYPE_COMPONENTS[field.field_type] || {};

    return (
      <TextField
        key={key}
        fullWidth
        label={`${label}${field.required ? ' *' : ''}`}
        value={formValues[key] ?? ''}
        onChange={(event) => updateField(key, event.target.value)}
        helperText={errorMessage || helper}
        error={Boolean(errorMessage)}
        sx={{ mb: 3 }}
        {...componentProps}
      />
    );
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, boxShadow: '0 24px 55px rgba(13,36,65,0.12)' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading your document…
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 6, borderRadius: 4, boxShadow: '0 24px 55px rgba(13,36,65,0.12)' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Please contact the sender to request a new link or confirm your access.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (completed) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 6, borderRadius: 4, boxShadow: '0 24px 55px rgba(13,36,65,0.12)' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            Signature captured
          </Typography>
          <Alert severity="success" sx={{ mb: 3 }}>
            Thank you! Your signed document has been submitted. A confirmation email will arrive
            shortly if the sender configured notifications.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Timestamp: {DEFAULT_TIME_FORMAT(new Date().toISOString())}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            IP Address (captured): {ipAddress || 'Recorded by server'}
          </Typography>
          <Divider sx={{ my: 4 }} />
          <Typography variant="body2" color="text.secondary">
            You can safely close this window. For a copy of the signed document, contact the sender
            or use the download link included in your confirmation message.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const recipient = submission?.assigned_to || submission?.recipient_email || accessHint;
  const dueDate = submission?.due_date;
  const issuer = submission?.assigned_by || submission?.issuer || null;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
      <Stack spacing={4}>
        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 5,
            boxShadow: '0 24px 55px rgba(13,36,65,0.12)',
            border: '1px solid rgba(13,36,65,0.08)'
          }}
        >
          <Stack spacing={2} sx={{ mb: 4 }}>
            <Typography variant="overline" color="primary" sx={{ letterSpacing: 4 }}>
              Dailey Forms Signature Request
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 600 }}>
              {template?.name || submission?.title || 'Document Signature'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review the fields below, add any requested information, and provide your electronic
              signature to complete this document.
            </Typography>
            <Stack spacing={1} direction={{ xs: 'column', md: 'row' }} divider={<Divider orientation="vertical" flexItem />}>
              {recipient && (
                <Typography variant="body2" color="text.secondary">
                  For: <strong>{recipient}</strong>
                </Typography>
              )}
              {issuer && (
                <Typography variant="body2" color="text.secondary">
                  Sent by: <strong>{issuer}</strong>
                </Typography>
              )}
              {dueDate && (
                <Typography variant="body2" color="text.secondary">
                  Due: <strong>{DEFAULT_TIME_FORMAT(dueDate)}</strong>
                </Typography>
              )}
            </Stack>
          </Stack>

          {attachments.length > 0 && (
            <Alert severity="info" sx={{ mb: 4 }} icon={<ArticleIcon fontSize="inherit" />}>
              This packet includes reference PDFs. Review them before signing:
              <List dense>
                {attachments.map((attachment) => (
                  <ListItem key={attachment.id || attachment.url || attachment.name}>
                    <ListItemIcon>
                      <DownloadIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        (() => {
                          const href = attachment.url || attachment.signedUrl || attachment.href;
                          const label = attachment.name || attachment.filename || 'Download attachment';
                          if (!href) {
                            return label;
                          }
                          return (
                            <Link href={href} target="_blank" rel="noopener">
                              {label}
                            </Link>
                          );
                        })()
                      }
                      secondary={
                        attachment.url || attachment.signedUrl || attachment.href
                          ? null
                          : 'Attachment currently unavailable. Contact the sender if you need assistance.'
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            {orderedFields.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 3 }}>
                This document does not contain any fillable fields. Please confirm with your sender
                before signing.
              </Alert>
            ) : (
              orderedFields.map((field, index) => renderField(field, index))
            )}

            <Divider sx={{ my: 4 }} />

            <Stack spacing={2} sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(event) => {
                      setAcknowledged(event.target.checked);
                      if (event.target.checked) {
                        setAckError(null);
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree that my electronic signature has the same legal effect as a handwritten
                    signature for this document.
                  </Typography>
                }
              />
              {ackError && (
                <FormHelperText error sx={{ ml: 3 }}>
                  {ackError}
                </FormHelperText>
              )}
            </Stack>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{
                mt: 1,
                px: 5,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 18
              }}
            >
              {submitting ? 'Submitting…' : 'Submit Signature'}
            </Button>
          </Box>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            bgcolor: 'rgba(13,36,65,0.03)'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Compliance & Security
          </Typography>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2} alignItems="center">
              <ShieldIcon color="primary" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Signatures are encrypted and time-stamped. IP address and device details are logged
                for audit trails.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <LockIcon color="primary" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Only the intended recipient should complete this form. Forwarding this link may
                compromise compliance obligations.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <PolicyIcon color="primary" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                By signing, you acknowledge that you reviewed the entire packet and agree to the
                contents described.
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

export default PublicFormSigning;
